/**
 * Application Routes
 *
 * Defines the complete route structure for the BlueRide Companies Dashboard.
 * Uses lazy loading for all feature modules to optimize initial load time.
 * Protected routes use authGuard, role-specific routes use roleGuard.
 *
 * Route Structure:
 * - /auth/* - Authentication routes (login, forgot password, reset password)
 * - / - Protected routes requiring authentication
 *   - /dashboard - Dashboard overview
 *   - /schools - School management
 *   - /reports - Report views with nested routes
 *   - /profile - User profile management
 *   - /admin-management - Admin management (SuperAdmin only)
 *   - /branch-view/* - Branch view mode (SuperAdmin only, includes branch management)
 */

import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';

export const routes: Routes = [
  // Default route redirects to dashboard
  {
    path: '',
    redirectTo: '/branch-view/dashboard',
    pathMatch: 'full'
  },

  // Authentication routes (public)
  {
    path: 'auth',
    loadChildren: () =>
      import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES)
  },

  // Protected routes (require authentication)
  {
    path: '',
    canActivate: [authGuard],
    children: [
      // Dashboard
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./features/dashboard/dashboard.routes').then(
            (m) => m.DASHBOARD_ROUTES
          )
      },

      // Schools
      {
        path: 'schools',
        loadChildren: () =>
          import('./features/schools/schools.routes').then(
            (m) => m.SCHOOL_ROUTES
          )
      },

      // Reports
      {
        path: 'reports',
        loadChildren: () =>
          import('./features/reports/reports.routes').then(
            (m) => m.REPORT_ROUTES
          )
      },

      // Profile
      {
        path: 'profile',
        loadChildren: () =>
          import('./features/profile/profile.routes').then(
            (m) => m.PROFILE_ROUTES
          )
      },

      // Admin Management (SuperAdmin only)
      {
        path: 'admin-management',
        canActivate: [roleGuard],
        data: { role: 'comp_superadmin' },
        loadChildren: () =>
          import('./features/admin-management/admin.routes').then(
            (m) => m.ADMIN_ROUTES
          )
      },

      // Branch View mode (SuperAdmin or Admin with multiple categories)
      {
        path: 'branch-view',
        canActivate: [roleGuard],
        data: { role: ['comp_superadmin', 'comp_admin'] },
        loadChildren: () =>
          import('./features/branch-view/branch-view.routes').then(
            (m) => m.BRANCH_VIEW_ROUTES
          )
      }
    ]
  },

  // Wildcard route - redirects to dashboard
  {
    path: '**',
    redirectTo: '/branch-view/dashboard'
  }
];
